import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  useTheme,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { tokens } from "../../theme";
import useAccountantService from "../../api/services/AccountantService";
import useStudentService from "../../api/services/studentService";
import useTermService from "../../api/services/termService";
import Header from "../../components/Header";
import CreateNewAccountDetails from "./CreateNewAccountDetails";
import EditAccountDetails from "./EditAccountDetails";
import DeleteAccountantDialog from "./DeleteAccountantDialog";
import { getFirstTermDebtLabel } from "../../utils/utils";

/** Same formula as ClassStudentsAccountsData: totalFees - totalPaid. Use item.debtBalance when present. */
const getDebtBalanceFromItem = (item) => {
  if (item.debtBalance !== null && item.debtBalance !== undefined && item.debtBalance !== "") {
    const n = parseFloat(item.debtBalance);
    if (!isNaN(n)) return n;
  }
  const toNum = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };
  const totalPaid = toNum(item.firstTermPaidAmount) + toNum(item.secondTermPaidAmount);
  const totalFees =
    toNum(item.firstTermDebt) + toNum(item.secondTermDebt) +
    toNum(item.firstTermFee) + toNum(item.secondTermFee) +
    toNum(item.firstTermExamFee) + toNum(item.secondTermExamFee) +
    toNum(item.firstTermOtherContribution) + toNum(item.secondTermOtherContribution) +
    toNum(item.firstTermParentFee) + toNum(item.secondTermParentFee);
  const balance = totalFees - totalPaid;
  return isNaN(balance) ? 0 : balance;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    value === null || value === undefined || isNaN(value) ? 0 : Number(value)
  );

/**
 * Newest-first ordering uses creation time only (not updatedAt).
 * API may send `createdAt` or `created_at`. Missing dates fall back to numeric id (higher ≈ newer).
 */
const getCreatedAtMs = (item) => {
  if (!item) return null;
  const raw = item.createdAt ?? item.created_at;
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return isNaN(t) ? null : t;
};

const getRowNewestFirstKey = (item) => {
  const ms = getCreatedAtMs(item);
  if (ms != null) return ms;
  const idNum = Number(item?.id);
  return !isNaN(idNum) ? idNum : 0;
};

const StudentAccountDetails = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { studentId } = useParams();
  const navigate = useNavigate();

  const { getAccountantDataByStudent, deleteAccountantDataById, isLoading: isServiceLoading } = useAccountantService();
  const { loadStudentDetails } = useStudentService();
  const { fetchAllActiveTerms, terms } = useTermService();

  const [accountants, setAccountants] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAccountantData, setEditingAccountantData] = useState(null);
  const [deletingRow, setDeletingRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sortModel, setSortModel] = useState([{ field: "_createdMs", sort: "desc" }]);

  // Fetch student info
  const loadStudent = async () => {
    try {
      const data = await loadStudentDetails(studentId);
      setSelectedStudent(data);
    } catch (err) {
      toast.error("Failed to fetch student information");
    }
  };

  // Fetch all account details for this student
  const loadAccountants = async () => {
    try {
      setLoading(true);
      const data = await getAccountantDataByStudent(studentId);
      console.log("Accountants data received:", data);
      setAccountants(data || []);
    } catch (error) {
      toast.error("Failed to fetch student account details");
      console.error("Error loading accountants:", error);
      setAccountants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentId) {
      loadStudent();
      loadAccountants();
      fetchAllActiveTerms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  // Handle edit row click
  const handleEditRow = (rowData) => {
    if (rowData?.originalData) {
      setEditingAccountantData(rowData.originalData);
      setEditDialogOpen(true);
    } else {
      toast.error("Cannot edit: Accountant record data is missing");
    }
  };

  // Handle view row click
  const handleViewRow = (rowData) => {
    if (rowData?.originalData?.id) {
      navigate(`/view-student-accountant-details/${rowData.originalData.id}`);
    } else {
      toast.error("Cannot view: Accountant record ID is missing");
    }
  };

  // Handle delete row click
  const handleDeleteRow = (rowData) => {
    setDeletingRow(rowData);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (deletingRow && deletingRow.originalData) {
      const accountantId = deletingRow.originalData.id;
      if (!accountantId) {
        toast.error("Cannot delete: Accountant record ID is missing");
        setDeleteDialogOpen(false);
        setDeletingRow(null);
        return;
      }

      try {
        await deleteAccountantDataById(accountantId);
        toast.success("Accountant record deleted successfully");
        setDeleteDialogOpen(false);
        setDeletingRow(null);
        // Reload accountants after deletion
        await loadAccountants();
      } catch (error) {
        console.error("Error deleting accountant record:", error);
        toast.error("Failed to delete accountant record: " + (error.response?.data?.message || error.message));
      }
    }
  };

  // Columns for DataGrid
  const columns = useMemo(() => [
    {
      field: "_createdMs",
      headerName: "",
      type: "number",
      width: 0,
      minWidth: 0,
      maxWidth: 0,
      sortable: true,
      filterable: false,
      disableColumnMenu: true,
      resizable: false,
    },
    { field: "termName", headerName: "Term", flex: 1, minWidth: 100 },
    {
      field: "firstTermDebt",
      headerName: getFirstTermDebtLabel(selectedStudent?.classTitle, { short: true }),
      flex: 1,
      minWidth: 80,
    },
    { field: "secondTermDebt", headerName: "T-2-Debt", flex: 1, minWidth: 80 },
    { field: "firstTermFee", headerName: "T-1-Fee", flex: 1, minWidth: 80 },
    { field: "secondTermFee", headerName: "T-2-Fee", flex: 1, minWidth: 80 },
    { field: "firstTermExamFee", headerName: "T-1-ExamFee", flex: 1, minWidth: 80 },
    { field: "secondTermExamFee", headerName: "T-2-ExamFee", flex: 1, minWidth: 80 },
    { field: "firstTermOtherContribution", headerName: "T-1-OtherContribution", flex: 1, minWidth: 80 },
    { field: "secondTermOtherContribution", headerName: "T-2-OtherContribution", flex: 1, minWidth: 80 },
    { field: "firstTermParentFee", headerName: "T-1-ParentFee", flex: 1, minWidth: 80 },
    { field: "secondTermParentFee", headerName: "T-2-ParentFee", flex: 1, minWidth: 80 },
    { field: "firstTermPaidAmount", headerName: "T-1-PaidAmount", flex: 1, minWidth: 80 },
    { field: "secondTermPaidAmount", headerName: "T-2-PaidAmount", flex: 1, minWidth: 80 },
    {
      field: "debtBalance",
      headerName: "Debt_Balance",
      flex: 1,
      minWidth: 90,
      renderCell: (params) => {
        const balance = params.row.debtBalance;
        const numBalance = typeof balance === "number" ? balance : parseFloat(balance);
        const display = formatCurrency(isNaN(numBalance) ? 0 : numBalance);
        return (
          <Typography
            sx={{
              color: numBalance > 0 ? colors.redAccent[500] : numBalance < 0 ? colors.greenAccent[500] : "inherit",
              fontWeight: "bold",
            }}
          >
            {display}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            onClick={() => handleViewRow(params.row)}
            color="info"
            title="View Account Details"
          >
            <VisibilityIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleEditRow(params.row)}
            color="primary"
            title="Edit Account Details"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteRow(params.row)}
            color="error"
            title="Delete Account Details"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ], [colors, selectedStudent?.classTitle]);

  // Prepare rows - use actual ID from data for proper deletion; sort so newest (last created) is on top
  const rows = useMemo(() => {
    if (!accountants || !Array.isArray(accountants)) {
      console.log("No accountants data or not an array:", accountants);
      return [];
    }

    const sorted = [...accountants].sort(
      (a, b) => getRowNewestFirstKey(b) - getRowNewestFirstKey(a)
    );

    console.log("Processing accountants, count:", sorted.length);
    const processedRows = sorted.map((item) => {
      return {
        id: item.id || `temp-${item.termId}-${item.studentId}`,
        _createdMs: getRowNewestFirstKey(item),
        termName: item.term?.termName || item.termName || "N/A",
        firstTermDebt: item.firstTermDebt !== null && item.firstTermDebt !== undefined ? Number(item.firstTermDebt) : 0,
        secondTermDebt: item.secondTermDebt !== null && item.secondTermDebt !== undefined ? Number(item.secondTermDebt) : 0,
        firstTermFee: item.firstTermFee !== null && item.firstTermFee !== undefined ? item.firstTermFee : 0,
        secondTermFee: item.secondTermFee !== null && item.secondTermFee !== undefined ? item.secondTermFee : 0,
        firstTermExamFee: item.firstTermExamFee !== null && item.firstTermExamFee !== undefined ? item.firstTermExamFee : 0,
        secondTermExamFee: item.secondTermExamFee !== null && item.secondTermExamFee !== undefined ? item.secondTermExamFee : 0,
        firstTermOtherContribution: item.firstTermOtherContribution !== null && item.firstTermOtherContribution !== undefined ? item.firstTermOtherContribution : 0,
        secondTermOtherContribution: item.secondTermOtherContribution !== null && item.secondTermOtherContribution !== undefined ? item.secondTermOtherContribution : 0,
        firstTermParentFee: item.firstTermParentFee !== null && item.firstTermParentFee !== undefined ? item.firstTermParentFee : 0,
        secondTermParentFee: item.secondTermParentFee !== null && item.secondTermParentFee !== undefined ? item.secondTermParentFee : 0,
        firstTermPaidAmount: item.firstTermPaidAmount !== null && item.firstTermPaidAmount !== undefined ? item.firstTermPaidAmount : 0,
        secondTermPaidAmount: item.secondTermPaidAmount !== null && item.secondTermPaidAmount !== undefined ? item.secondTermPaidAmount : 0,
        debtBalance: getDebtBalanceFromItem(item),
        originalData: {
          ...item,
          id: item.id,
          termId: item.termId || item.term?.id,
          studentId: item.studentId || item.student?.id,
        },
      };
    });
    console.log("Processed rows:", processedRows);
    return processedRows;
  }, [accountants]);

  return (
    <Box m="20px">
      <Header
        title="STUDENT ACCOUNT DETAILS"
        subtitle="View and manage financial account details by term"
      />

      {/* Header Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" color={colors.grey[100]}>
            Student: {selectedStudent?.studentName || "Loading..."}
          </Typography>
        </Box>
        <Box>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => setDialogOpen(true)}
            sx={{ mr: 2 }}
            disabled={!selectedStudent || !terms || terms.length === 0}
          >
            Add / Update Account Details
          </Button>
        </Box>
      </Box>

      {/* Data Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress color="secondary" />
        </Box>
      ) : rows.length > 0 ? (
        <Box
          height="70vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-columnHeaders": { 
              backgroundColor: theme.palette.mode === 'light' ? colors.blueAccent[100] : colors.blueAccent[700], 
              color: theme.palette.mode === 'light' ? '#000' : '#fff' 
            },
            "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel(model)}
            initialState={{
              columns: {
                columnVisibilityModel: { _createdMs: false },
              },
            }}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } },
            }}
          />
        </Box>
      ) : (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          mt={4}
          p={4}
          sx={{
            backgroundColor: colors.primary[400],
            borderRadius: 2,
            border: `1px solid ${colors.grey[100]}`,
          }}
        >
          <Typography variant="h6" color={colors.grey[100]}>
            No account details found for this student. Click "Add / Update Account Details" to create a new record.
          </Typography>
        </Box>
      )}

      {/* Add/Edit Accounts Dialog */}
      {dialogOpen && selectedStudent && (
        <CreateNewAccountDetails
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setEditingRow(null);
          }}
          onSuccess={() => {
            setDialogOpen(false);
            setEditingRow(null);
            setTimeout(() => {
              loadAccountants();
            }, 500);
          }}
          studentId={studentId}
          studentName={selectedStudent.studentName}
          classId={selectedStudent.clazzId || selectedStudent.classId}
          classTitle={selectedStudent.classTitle}
          editingRow={editingRow}
        />
      )}

      {/* Edit Account Details Dialog */}
      {editDialogOpen && editingAccountantData && (
        <EditAccountDetails
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingAccountantData(null);
          }}
          onSuccess={() => {
            setEditDialogOpen(false);
            setEditingAccountantData(null);
            setTimeout(() => {
              loadAccountants();
            }, 500);
          }}
          accountantData={editingAccountantData}
          classTitle={selectedStudent?.classTitle}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <DeleteAccountantDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setDeletingRow(null);
          }}
          onConfirm={handleDeleteConfirm}
          deletingData={deletingRow}
          classTitle={selectedStudent?.classTitle}
          isLoading={isServiceLoading}
        />
      )}
    </Box>
  );
};

export default StudentAccountDetails;

